import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { EASE_LUXE } from "@/components/Reveal";
import { GoogleButton } from "@/pages/Login";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ username: "", email: "", password: "", account_type: "member" });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Deep-link support: /register?as=advertiser pre-selects the advertiser tier.
  useEffect(() => {
    const as = params.get("as");
    if (as === "advertiser") {
      setForm((f) => ({ ...f, account_type: "advertiser" }));
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form.username.trim(), form.email, form.password, form.account_type);
      navigate("/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6 pt-24" data-testid="register-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE_LUXE }} className="w-full max-w-md">
        <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">Join Vixera</p>
        <h1 className="font-heading text-5xl font-semibold leading-none tracking-tight text-white">Create account</h1>

        <div className="mt-10"><GoogleButton label="Sign up with Google" /></div>
        <div className="my-8 flex items-center gap-4 text-[10px] uppercase tracking-luxe text-white/30">
          <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
        </div>

        <form onSubmit={submit} className="space-y-8">
          <div>
            <label className="mb-2 block text-xs uppercase tracking-luxe text-white/40">Username (your display name)</label>
            <input required minLength={3} maxLength={24} value={form.username} data-testid="register-username"
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="w-full border-b border-white/15 bg-transparent py-3 text-sm text-white focus:border-velvet-gold focus:outline-none" />
          </div>
          <div>
            <label className="mb-2 block text-xs uppercase tracking-luxe text-white/40">Email</label>
            <input type="email" required value={form.email} data-testid="register-email"
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border-b border-white/15 bg-transparent py-3 text-sm text-white focus:border-velvet-gold focus:outline-none" />
          </div>
          <div>
            <label className="mb-2 block text-xs uppercase tracking-luxe text-white/40">Password</label>
            <div className="relative">
              <input type={showPw ? "text" : "password"} required minLength={6} value={form.password} data-testid="register-password"
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full border-b border-white/15 bg-transparent py-3 pr-10 text-sm text-white focus:border-velvet-gold focus:outline-none" />
              {form.password && (
                <button type="button" onClick={() => setShowPw((v) => !v)}
                  data-testid="register-show-password"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-0 top-1/2 -translate-y-1/2 p-1 text-white/50 hover:text-velvet-gold">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="mb-3 block text-xs uppercase tracking-luxe text-white/40">Account type</label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { v: "member", t: "Member", d: "Browse & message" },
                { v: "advertiser", t: "Advertiser", d: "Place listings" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setForm({ ...form, account_type: opt.v })}
                  data-testid={`register-account-type-${opt.v}`}
                  className={`border p-4 text-left transition-colors ${
                    form.account_type === opt.v
                      ? "border-velvet-gold bg-velvet-gold/5"
                      : "border-white/15 hover:border-white/30"
                  }`}
                >
                  <p className={`text-sm ${form.account_type === opt.v ? "text-velvet-gold" : "text-white"}`}>{opt.t}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-luxe text-white/40">{opt.d}</p>
                </button>
              ))}
            </div>
          </div>
          {error && <p className="text-sm text-velvet-rose" data-testid="register-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="register-submit"
            className="w-full rounded-full bg-white py-4 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-rose hover:text-white disabled:opacity-50">
            {loading ? "Creating…" : "Create account"}
          </button>
        </form>

        <p className="mt-8 text-sm font-light text-white/50">
          Already a member? <Link to="/login" className="text-velvet-gold">Sign in</Link>
        </p>
      </motion.div>
    </div>
  );
}
